const PARTNER_STORAGE_KEY = 'vinr_current_partner';

export interface PartnerConfig {
  name: string;
  displayName: string;
  primaryColor: string;
  secondaryColor: string;
  logoPath: string;
  description: string;
}

export const PARTNER_CONFIGS: Record<string, PartnerConfig> = {
  postbank: {
    name: 'postbank',
    displayName: 'Postbank',
    primaryColor: '#133890',
    secondaryColor: '#ffffff',
    logoPath: '/images/postbank.png',
    description: 'Advanced banking solutions for modern businesses'
  },
  'texim-bank': {
    name: 'texim-bank',
    displayName: 'Texim Bank',
    primaryColor: '#000000e8',
    secondaryColor: '#ffffff',
    logoPath: '/images/texim_bank.png',
    description: 'Innovative financial services for growing enterprises'
  },
  'procredit-bank': {
    name: 'procredit-bank',
    displayName: 'ProCredit Bank',
    primaryColor: '#000000e8',
    secondaryColor: '#ffffff',
    logoPath: '/images/procredit_bank.png',
    description: 'Sustainable banking for responsible businesses'
  }
};

/**
 * Save current partner to localStorage
 */
export function setCurrentPartner(partnerName: string): void {
  try {
    localStorage.setItem(PARTNER_STORAGE_KEY, partnerName);
  } catch (error) {
    console.warn('Failed to save partner to localStorage:', error);
  }
}

/**
 * Get current partner from localStorage
 */
export function getCurrentPartner(): string | null {
  try {
    return localStorage.getItem(PARTNER_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to read partner from localStorage:', error);
    return null;
  }
}

/**
 * Get partner configuration by name
 */
export function getPartnerConfig(partnerName: string | null): PartnerConfig | null {
  if (!partnerName || !PARTNER_CONFIGS[partnerName]) {
    return null;
  }
  return PARTNER_CONFIGS[partnerName];
}

/**
 * Get partner configuration with fallback
 */
export function getPartnerConfigWithFallback(partnerName: string | null): PartnerConfig {
  const config = getPartnerConfig(partnerName);
  if (config) {
    return config;
  }

  // Default fallback configuration
  return {
    name: partnerName || 'default',
    displayName: partnerName ? partnerName.charAt(0).toUpperCase() + partnerName.slice(1) : 'Partner',
    primaryColor: '#7B2CBF',
    secondaryColor: '#ffffff',
    logoPath: partnerName ? `/images/${partnerName}-logo.png` : '/images/vinr-dark.svg',
    description: 'Advanced usage-based billing solutions'
  };
}

/**
 * Get partner logo based on sidebar state
 */
export function getPartnerLogo(partnerName: string | null, isCollapsed: boolean = false): string {
  if (!partnerName) {
    return '/images/vinr-dark.svg';
  }

  switch (partnerName) {
    case 'postbank':
      return isCollapsed ? '/images/postbank_small.png' : '/images/postbank.png';
    case 'texim-bank':
      return isCollapsed ? '/images/texim_small.png' : '/images/texim_bank.png';
    case 'procredit-bank':
      return isCollapsed ? '/images/procredit_small.png' : '/images/procredit_bank.png';
    default:
      return '/images/vinr-dark.svg';
  }
}

/**
 * Get partner logo height class based on partner
 */
export function getPartnerLogoHeight(partnerName: string | null): string {
  if (!partnerName || partnerName === 'default') {
    return 'h-6';
  }
  return 'h-8';
}

/**
 * Get partner logo alt text
 */
export function getPartnerLogoAlt(partnerName: string | null): string {
  if (!partnerName) {
    return 'Vinr logo';
  }
  const config = getPartnerConfig(partnerName);
  return config ? `${config.displayName} logo` : `${partnerName} logo`;
}

/**
 * Clear current partner from localStorage
 */
export function clearCurrentPartner(): void {
  try {
    localStorage.removeItem(PARTNER_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear partner from localStorage:', error);
  }
}